import { fetcher } from '@/lib/api';
import { 
  BlocoHorario, 
  CreateBlocoHorarioRequest, 
  UpdateBlocoHorarioRequest, 
  BlocoHorarioResponse,
  BlocoHorarioStatistics 
} from '@/types/blocoHorario';

export class BlocoHorarioService {
  static async getBlocosHorarios(
    page: number = 1,
    limit: number = 20,
    salaId?: string,
    ativo?: boolean
  ): Promise<BlocoHorarioResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (salaId) params.append('sala_id', salaId);
    if (ativo !== undefined) params.append('ativo', ativo.toString());
    const url = `/api/blocos-horarios?${params.toString()}`;
    return await fetcher<BlocoHorarioResponse>({ method: 'get', url });
  }

  static async getBlocoById(id: string): Promise<BlocoHorario> {
    return await fetcher<BlocoHorario>({ method: 'get', url: `/api/blocos-horarios/${id}` });
  }

  static async getBlocosBySala(salaId: string, ativo?: boolean): Promise<BlocoHorario[]> {
    const params = new URLSearchParams();
    if (ativo !== undefined) {
      params.append('ativo', ativo.toString());
    }

    const response = await fetcher<BlocoHorario[]>({ method: 'get', url: `/api/blocos-horarios/sala/${salaId}?${params.toString()}` });
    return response;
  }

  static async getStatistics(): Promise<BlocoHorarioStatistics> {
    const response = await fetcher<BlocoHorarioStatistics>({ method: 'get', url: '/api/blocos-horarios/statistics' });
    return response;
  }

  static async createBlocoHorario(data: CreateBlocoHorarioRequest): Promise<BlocoHorario> {
    const response = await fetcher<BlocoHorario>({ method: 'post', url: '/api/blocos-horarios', data });
    return response;
  }

  static async updateBlocoHorario(id: string, data: UpdateBlocoHorarioRequest): Promise<BlocoHorario> {
    const response = await fetcher<BlocoHorario>({ method: 'put', url: `/api/blocos-horarios/${id}`, data });
    return response;
  }

  static async deleteBlocoHorario(id: string): Promise<void> {
    await fetcher({ method: 'delete', url: `/api/blocos-horarios/${id}` });
  }

  static async toggleStatus(id: string): Promise<BlocoHorario> {
    const response = await fetcher<BlocoHorario>({ method: 'patch', url: `/api/blocos-horarios/${id}/toggle-status` });
    return response;
  }

  // Método para agrupar blocos por sala
  static groupBlocosBySala(blocos: BlocoHorario[]): { [key: string]: BlocoHorario[] } {
    return blocos.reduce((acc, bloco) => {
      const salaNome = bloco.sala?.nome || 'Sala sem nome';
      if (!acc[salaNome]) {
        acc[salaNome] = [];
      }
      acc[salaNome].push(bloco);
      return acc;
    }, {} as { [key: string]: BlocoHorario[] });
  }

  // Método auxiliar para calcular duração em minutos
  static calculateDuration(horaInicio: string, horaFim: string): number {
    // ✅ FIX: Validação para evitar erro com valores undefined
    if (!horaInicio || !horaFim || typeof horaInicio !== 'string' || typeof horaFim !== 'string') {
      return 0;
    }
    
    try {
      const [inicioH, inicioM] = horaInicio.split(':').map(Number);
      const [fimH, fimM] = horaFim.split(':').map(Number);
      
      // Validação adicional para garantir que os valores são números válidos
      if (isNaN(inicioH) || isNaN(inicioM) || isNaN(fimH) || isNaN(fimM)) {
        return 0;
      }
      
      const inicioMinutos = inicioH * 60 + inicioM;
      const fimMinutos = fimH * 60 + fimM;
      
      return fimMinutos - inicioMinutos;
    } catch (error) {
      console.warn('Erro ao calcular duração:', error);
      return 0;
    }
  }

  // Método auxiliar para formatar duração
  static formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    
    return `${hours}h ${remainingMinutes}min`;
  }

  // Método auxiliar para obter token
  private static getToken(): string {
    return localStorage.getItem('authToken') || '';
  }

  // Novos métodos para geração de blocos
  static async estimarGeracaoBlocos(): Promise<{ estimativa: number; relatorio: unknown }> {
    const response = await fetcher<{ estimativa: number; relatorio: unknown }>({ method: 'get', url: '/api/blocos-horarios/geracao/estimativa' });
    return response;
  }

  static async gerarBlocosProximos365Dias(): Promise<unknown> {
    const response = await fetcher<unknown>({ method: 'post', url: '/api/blocos-horarios/gerar' });
    return response;
  }

  static async limparBlocosPassados(): Promise<unknown> {
    const response = await fetcher<unknown>({ method: 'delete', url: '/api/blocos-horarios/geracao/limpar' });
    return response;
  }

  // Novo método para buscar disponibilidade por data
  static async getDisponibilidadePorData(data: string): Promise<unknown> {
    const response = await fetcher<unknown>({ method: 'get', url: `/api/blocos-horarios/disponibilidade/${data}` });
    return response;
  }
} 
